import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ApplicationActionCellRendererParams } from './models/application-action-cell-renderer-params';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import {
    ApplicationSubmitToPscaResponse,
    ApplicationSubmitToPscaStatus,
} from '../../../../../../modules/application/direct-entry/shared/models/application-submit-to-psca-response';
import { Observable, tap, catchError, EMPTY } from 'rxjs';
import {
    ApplicationSubmitToFccResponse,
    ApplicationSubmitToFccStatus,
} from '../../../../../../modules/application/shared/models/responses/application-submit-to-fcc-response';
import { MessageConstants } from '../../../../../../core/constants/message-constants';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { ApplicationSubmitToFccService } from '../../../../../../modules/application/shared/services/application-submit-to-fcc/application-submit-to-fcc.service';
import { ApplicationSubmitToPscaService } from '../../../../../../modules/application/direct-entry/shared/services/application-submit-to-psca/application-submit-to-psca.service';
import { SubmissionToFccCompleteDialogMessage } from '../../../../../../modules/application/shared/constants/submission-dialog-message';

@Component({
    selector: 'app-application-action-cell-renderer',
    templateUrl: './application-action-cell-renderer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonModule],
})
export class ApplicationActionCellRendererComponent
    implements ICellRendererAngularComp
{
    params!: ApplicationActionCellRendererParams;

    constructor(
        private readonly router: Router,
        private readonly returnRouteService: ReturnRouteService,
        private readonly applicationSubmitToFccService: ApplicationSubmitToFccService,
        private readonly applicationSubmitToPscaService: ApplicationSubmitToPscaService,
        private readonly alertService: AlertService,
    ) {}

    agInit(params: ApplicationActionCellRendererParams): void {
        this.params = params;
    }

    refresh(): boolean {
        return false;
    }

    navigate(): void {
        this.returnRouteService.set(this.params.returnRoute ?? null);
        this.router.navigate([this.getRouterLink()]);
    }

    submitToPsca(): void {
        this.handleSubmission(
            this.applicationSubmitToPscaService.submitToPsca(
                this.params.data.applicationId.toString(),
                this.params.data.customerUserId,
                false,
            ),
            ApplicationSubmitToPscaStatus.Submitted,
        );
    }

    submitToFcc(): void {
        this.handleSubmission(
            this.applicationSubmitToFccService.submitToFcc(
                this.params.data.applicationId.toString(),
                this.params.data.customerUserId,
                false,
            ),
            ApplicationSubmitToFccStatus.Submitted,
        );
    }

    private getRouterLink(): string {
        return RouteConstants.Application.getFullRouteWithApplicationId(
            'coordinationParameters',
            this.params.data.applicationId.toString(),
        );
    }

    private handleSubmission(
        submission$: Observable<
            ApplicationSubmitToPscaResponse | ApplicationSubmitToFccResponse
        >,
        expectedStatus:
            | ApplicationSubmitToPscaStatus
            | ApplicationSubmitToFccStatus,
    ): void {
        submission$
            .pipe(
                tap((response) => {
                    if (response.submissionStatus === expectedStatus) {
                        this.alertService.displaySuccessAlert(
                            SubmissionToFccCompleteDialogMessage.modalTitle,
                        );

                        this.updateFilingStatusAndRefreshRow(
                            response.filingStatus,
                        );

                        if (this.params.onSubmitSuccess) {
                            this.params.onSubmitSuccess();
                        }
                    }
                }),
                catchError(() => {
                    this.alertService.displayErrorAlert(
                        MessageConstants.apiErrorMesage,
                        true,
                    );
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    get buttonConfig(): { label: string; action: () => void } | null {
        if (this.params.data.isCoordinationAllowed) {
            return {
                label: NavigationText.COORDINATE,
                action: () => this.navigate(),
            };
        }
        if (this.params.data.isSubmitToPscaAllowed) {
            return {
                label: NavigationText.SUBMIT_TO_PSCA,
                action: () => this.submitToPsca(),
            };
        }
        if (this.params.data.isSubmitToFccAllowed) {
            return {
                label: NavigationText.SUBMIT_TO_FCC,
                action: () => this.submitToFcc(),
            };
        }
        return null;
    }

    /**
     * Updates the filing status and isSubmit flags for the current grid row
     * and refreshes the row to reflect the change.
     *
     * @param filingStatus - The new filing status to set
     */
    private updateFilingStatusAndRefreshRow(filingStatus: string): void {
        this.params.data.filingStatus = filingStatus;
        this.params.data.isSubmitToPscaAllowed = false;
        this.params.data.isSubmitToFccAllowed = false;

        // Refresh the specific cell to reflect the updated status
        this.params.api.refreshCells({
            rowNodes: [this.params.node],
            force: true,
        });

        // Notify the grid that a filter change may have occurred
        this.params.api.onFilterChanged();
    }
}
